import LockOpenIcon from '@mui/icons-material/LockOpen';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import type { WallBoard, WallId } from '../../../store/types';

interface WallPasswordDialogProps {
  board: Pick<WallBoard, 'id' | 'title' | 'isPublic'> | null;
  boardId: WallId;
  error: string | null;
  isAuthenticating: boolean;
  open: boolean;
  onAuthenticate: (boardId: WallId, password: string) => Promise<void>;
}

export function WallPasswordDialog({
  board,
  boardId,
  error,
  isAuthenticating,
  open,
  onAuthenticate,
}: WallPasswordDialogProps) {
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setPassword('');
    setLocalError(null);
  }, [open]);

  const handleSubmit = async () => {
    const trimmedPassword = password.trim();

    if (!trimmedPassword) {
      setLocalError('Please provide a password');
      return;
    }

    setLocalError(null);
    await onAuthenticate(board?.id ?? boardId, trimmedPassword);
  };

  return (
    <Dialog open={open} disableEscapeKeyDown fullWidth maxWidth="xs">
      <DialogTitle>Password Protected Wall</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {board?.title ? <Typography variant="subtitle1">{board.title}</Typography> : null}
          {localError ? <Alert severity="error">{localError}</Alert> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            type="password"
            label="Password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleSubmit();
              }
            }}
            autoFocus
            disabled={isAuthenticating}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => void handleSubmit()}
          variant="contained"
          disabled={isAuthenticating}
          startIcon={<LockOpenIcon />}
        >
          Enter Wall
        </Button>
      </DialogActions>
    </Dialog>
  );
}
